// eslint-disable-next-line monorepo/no-internal-import
import {PageReadyHint} from '@aboutyou/bootstrap/src/pageReadyHint/PageReadyHint';
import {useIsSoftFeatureToggleActive} from '@aboutyou/feature-flags';
import {useHydrationDone} from '@aboutyou/lazy-imports';
import {
  ActivateExcludedAssortmentABTest,
  AdBlockDetection,
  BrowserMemoryConsumptionTracker,
  CheckoutPreloadConditional,
  ConsentBannerFocusManagement,
  CountryDetection,
  CouponTopBanner,
  CouponsRefresh,
  DatadogLogsRouteTracker,
  DisableAddToHomeScreen,
  DiscountBanner,
  DiscountsRefresh,
  FooterLayout,
  GuestCheckoutSessionRemover,
  HelpCenterChatbotOpener,
  InitializeAbTestsConditionally,
  LoadCustomerDataConditional,
  RegisterDataFetcherSideEffects,
  SearchResultQuestionnaire,
  SlideInWebCouponBanner,
  SponsoredProductPopup,
  SubscribeToWhatsappNewsletter,
  UnlockCouponInfo,
  useFullScreenView,
  useShopExitRedirect,
  useShowSlideInWebCouponBanner,
} from '@aboutyou/logic-components';
import {
  useHistory,
  useIsBasketPage,
  useIsCheckoutPage,
  useIsOrderSuccessPage,
} from '@aboutyou/router';
import {TrackCustomer} from '@aboutyou/tracking';
import {
  ClientGate,
  ErrorBoundary,
  ModalDialog,
  ScrollToTop,
  SkipToContentLink,
} from '@aboutyou/ui-components';
import {SyncSessionBetweenTabs} from '@aboutyou/user';
import React, {useRef} from 'react';
import {ConditionalSyncLegacyState} from '../bridge/SyncLegacyStateContext';
import {PhonePopupConditional} from '../bridge/global/PhonePopup/PhonePopup';
import {ToastNotifications} from '../bridge/global/ToastNotifications/ToastNotifications';
import {AppProviders} from './AppProviders';
import {RouteComponents} from './RouteComponents';
import {FooterDynamic} from './components/globals/Footer/FooterDynamic';
import {FooterDynamicADP} from './components/globals/Footer/FooterDynamicADP';
import {FooterStatic} from './components/globals/Footer/FooterStatic';
import {Header} from './components/globals/Header/Header';
import {ConditionalUspBanner} from './components/globals/UspBanner/ConditionalUspBanner';
import {Wallet} from './components/globals/Wallet';

export const App: React.FC = () => {
  const result = useShopExitRedirect();

  if (result.redirect) {
    return <>{result.redirect}</>;
  }

  return (
    <AppProviders>
      <DisableAddToHomeScreen />
      <SkipToContentLink />
      <DiscountsRefresh />
      <RegisterDataFetcherSideEffects />

      <ErrorBoundary renderContext="header_coupon_banner">
        <ConditionalCouponTopBanner />
      </ErrorBoundary>
      <ErrorBoundary renderContext="header_campaign_banner">
        <ConditionalDiscountBanner />
      </ErrorBoundary>

      <ConditionalHeader />
      <ConditionalUspBanner />
      <ErrorBoundary renderContext="route_components">
        <RouteComponents />
      </ErrorBoundary>
      <ConditionalFooter />

      <ErrorBoundary renderContext="ad_block_detection" ErrorComponent={null}>
        <AdBlockDetection />
      </ErrorBoundary>

      <ClientGate>
        <ErrorBoundary renderContext="coupons_refresh" ErrorComponent={null}>
          <CouponsRefresh />
        </ErrorBoundary>
        <LoadCustomerDataConditional />
        <ActivateExcludedAssortmentABTest />
        <ErrorBoundary renderContext="unlock_coupon" ErrorComponent={null}>
          <UnlockCouponInfo LayoutComponent={ModalDialog} />
        </ErrorBoundary>
        <SyncSessionBetweenTabs />
        <ErrorBoundary renderContext="country_detection" ErrorComponent={null}>
          <CountryDetectionConditionally />
        </ErrorBoundary>
        <ErrorBoundary
          renderContext="search_results_questionnaire"
          ErrorComponent={null}
        >
          <SearchResultQuestionnaire />
        </ErrorBoundary>

        <ErrorBoundary renderContext="web_coupon_banner" ErrorComponent={null}>
          <ConditionalSlideInWebCouponBanner />
        </ErrorBoundary>

        <ErrorBoundary renderContext="scroll_top" ErrorComponent={null}>
          <ScrollToTop position="right" />
        </ErrorBoundary>

        <ErrorBoundary renderContext="track_customer" ErrorComponent={null}>
          <TrackCustomer />
        </ErrorBoundary>

        <ConditionalPhonePopup />

        <ErrorBoundary renderContext="legacy_state_sync" ErrorComponent={null}>
          <ConditionalSyncLegacyState>
            {({mountLegacyComponents}) => {
              return mountLegacyComponents ? <ToastNotifications /> : null;
            }}
          </ConditionalSyncLegacyState>
        </ErrorBoundary>

        <DatadogLogsRouteTracker />
        <BrowserMemoryConsumptionTracker />
        <InitializeAbTestsConditionally />
        <SubscribeToWhatsappNewsletter />
        <CheckoutPreloadConditional />
        <GuestCheckoutSessionRemover />
        <PageReadyHint />
        <Wallet />
        <ErrorBoundary
          renderContext="help-center-chatbot"
          ErrorComponent={null}
        >
          <HelpCenterChatbotOpener />
        </ErrorBoundary>
        <ErrorBoundary
          renderContext="sponsored-product-popup"
          ErrorComponent={null}
        >
          <SponsoredProductPopup LayoutComponent={ModalDialog} />
        </ErrorBoundary>
        <ErrorBoundary
          renderContext="consent-banner-focus-management"
          ErrorComponent={null}
        >
          <ConsentBannerFocusManagement />
        </ErrorBoundary>
      </ClientGate>
    </AppProviders>
  );
};

const ConditionalPhonePopup = () => {
  const isHydrationDone = useHydrationDone();
  const isBasketPage = useIsBasketPage();
  if (!isHydrationDone || isBasketPage) {
    return null;
  }
  return <PhonePopupConditional />;
};

const ConditionalHeader = () => {
  const {isHeaderDisabled} = useFullScreenView();
  if (isHeaderDisabled) {
    return null;
  }
  return (
    <ErrorBoundary renderContext="header">
      <Header />
    </ErrorBoundary>
  );
};

const ConditionalCouponTopBanner = () => {
  const isCheckoutPage = useIsCheckoutPage();
  if (isCheckoutPage) {
    return null;
  }
  return <CouponTopBanner />;
};

const ConditionalDiscountBanner = () => {
  const isCheckoutPage = useIsCheckoutPage();
  if (isCheckoutPage) {
    return null;
  }
  return <DiscountBanner />;
};

const ConditionalFooter = () => {
  const {isFooterDisabled} = useFullScreenView();
  if (isFooterDisabled) {
    return null;
  }
  return (
    <ErrorBoundary renderContext="footer">
      <FooterLayout>
        <FooterStatic />
        <FooterDynamic />
        <FooterDynamicADP />
      </FooterLayout>
    </ErrorBoundary>
  );
};

const CountryDetectionConditionally = () => {
  const isInternationalPopupEnabled = useIsSoftFeatureToggleActive(
    'show_international_popup',
  );

  const history = useHistory();
  const params = new URLSearchParams(history.location.search);

  const isDisabledByUrlParam = useRef(
    params.get('disableCountrySwitch') === '1',
  ).current;

  const enteredThroughOSP = useRef(useIsOrderSuccessPage()).current;

  return isInternationalPopupEnabled &&
    !enteredThroughOSP &&
    !isDisabledByUrlParam ? (
    <CountryDetection />
  ) : null;
};

const ConditionalSlideInWebCouponBanner = () => {
  const shouldShowSlideInWebCouponBanner = useShowSlideInWebCouponBanner();
  return shouldShowSlideInWebCouponBanner ? <SlideInWebCouponBanner /> : null;
};
